import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Experiments } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { Badge } from '../Badge/Badge';
import { CycleCheckbox } from './CycleCheckbox';
import { AscendBundleIncludedNote } from './AscendBundleIncludedNote';
import { CycleHelper } from './CycleHelper';
import { CycleLabel } from './CycleLabel';
import { CyclePopoverProvider, CyclePopoverWrapper } from './CyclePopover';
import { CyclePrice } from './CyclePrice';
import { CyclesController } from './Cycles.controller';
import s from './Cycles.scss';
import { GridCell } from './GridCell';

export interface CyclesProps extends InjectedExperimentsProps {
  rootStore: RootStore;
}

const CyclesComp: React.FC<CyclesProps> = (props) => {
  const { appStore } = props.rootStore;
  const controller = React.useMemo(
    () => new CyclesController(props),
    [appStore.currentCycle, appStore.currentProduct],
  );
  const {
    appContext: { country },
    isInSale,
    isJapanesePackagePicker,
  } = appStore;
  const {
    cycles,
    isCycleSelected,
    showDisclaimer,
    disclaimerKey,
    missingFeatureList,
    getGridRowsConfig,
    maxPossibleCycle,
  } = controller;
  const { theme } = usePageModeContext();

  const isAscendBundleEnabled = props.experiments.enabled(
    Experiments.AscendBundle,
  );
  return (
    <CyclePopoverProvider cycles={cycles}>
      <div
        data-hook='cycles-wrapper'
        className={classNames(s.cycles, theme, {
          [s.showMonths]: isJapanesePackagePicker,
        })}
        style={getGridRowsConfig()}
      >
        {cycles.map(({ cellProps, cycleDetails }, index) => (
          <CyclePopoverWrapper key={index} cycleDetails={cycleDetails}>
            <React.Fragment>
              <GridCell padded='Left' {...cellProps}>
                <CycleCheckbox
                  cycleDetails={cycleDetails}
                  isCycleSelected={isCycleSelected(cycleDetails.cycleId)}
                />
                <CycleLabel
                  cycleId={cycleDetails.cycleId}
                  maxPossibleCycle={maxPossibleCycle}
                />
              </GridCell>
              <GridCell {...cellProps}>
                <span className={s.horizontalDash} />
              </GridCell>
              <GridCell {...cellProps}>
                <div className={s.priceNotes}>
                  <CyclePrice
                    cycleDetails={cycleDetails}
                    country={country}
                    isInSale={isInSale}
                  />
                  {isAscendBundleEnabled && cycleDetails.isAscendBundle && (
                    <AscendBundleIncludedNote isInSale={isInSale} />
                  )}
                </div>
              </GridCell>
              <GridCell padded='Right' {...cellProps}>
                <Badge
                  cycle={cycleDetails}
                  isCycleSelected={isCycleSelected(cycleDetails.cycleId)}
                />
              </GridCell>
              <GridCell {...cellProps} className={classNames(s.helper)}>
                <CycleHelper
                  isShown={isCycleSelected(cycleDetails.cycleId)}
                  cycle={cycleDetails.cycleId}
                  isAscendBundle={cycleDetails.isAscendBundle}
                  _disclaimerKey={disclaimerKey}
                  features={missingFeatureList}
                  showDisclaimer={showDisclaimer}
                />
              </GridCell>
            </React.Fragment>
          </CyclePopoverWrapper>
        ))}
      </div>
    </CyclePopoverProvider>
  );
};

export const Cycles = withInjectedRootStore(
  withExperiments(observer(CyclesComp)),
);
